import { useQuery } from '@tanstack/react-query';
import { getOffering } from 'services/offerings';

// Typescript
import { UseQueryInterface } from 'typescriptUtils/interfaces/reactQuery';
import { OfferingType } from 'typescriptUtils/types/offerings/offering';

interface UseGetOfferingResult extends UseQueryInterface<OfferingType> {
  isFetching: boolean;
}

/**
 * Single source of truth for offering metadata (status, regulationType,
 * pricePerShare, raised totals, etc.) across the BuilderLayout and
 * elsewhere. Funnels every consumer through the `['offeringData', slug]`
 * queryKey so React Query dedupes them to a single network fetch.
 *
 * History: this hook previously used `['offering', slug]` while
 * BuilderHeroSection / BuilderUpdatesSection / BuilderDiscussionSection
 * had inline `useQuery` calls keyed `['offeringData', slug]` — two
 * separate caches for the same data, two stale states, two refetch
 * triggers. PR #4206's review notes flagged this; this commit
 * reconciles them on the `['offeringData', slug]` key (the more
 * widespread one in inline consumers).
 *
 * `refetchOnMount: 'always'` ensures launch-day status transitions
 * propagate immediately on every page revisit (PD-000 / EVOQ Bio
 * Jun 2 2026 — saw "Reserve Now" stale for ~5 min after launch
 * because the cached `status: 'testing'` was served from the 5-min
 * staleTime window).
 *
 * The TTW → CF preview override (PD-35758) is applied at the data layer
 * (`getOffering` + the BuilderLayout cache seed), not here, so it reaches
 * every `['offeringData', slug]` consumer — including the many components
 * with their own inline `useQuery` on that key, not just this hook.
 *
 * Pass `enabled: false` to defer the fetch — used by lazy-mounted
 * sections that gate their data on `useInViewSticky`.
 */
const useGetOffering = ({
  slug,
  enabled = true,
}: {
  slug: string | string[];
  enabled?: boolean;
}): UseGetOfferingResult => {
  const normalizedSlug = Array.isArray(slug) ? slug[0] : slug;

  const { data, refetch, isLoading, isFetching, isSuccess, isError } = useQuery({
    queryKey: ['offeringData', normalizedSlug],
    queryFn: () => getOffering({ slug: normalizedSlug }),
    enabled: !!normalizedSlug && enabled,
    refetchOnMount: 'always',
    staleTime: 5 * 60 * 1000,
  });

  return {
    data: data as OfferingType | undefined,
    refetch,
    isLoading,
    isFetching,
    isSuccess,
    isError,
  };
};

export default useGetOffering;
